import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  api,
  type BoardAccessView,
  type BoardRoleName,
  type CallBanView,
} from '../lib/api';
import { authApi, inviteUrl, type OrgMemberView } from '../lib/authApi';
import { Popover } from '../ui/controls';
import { Icon } from '../ui/icons';

const ROLE_LABEL: Record<BoardRoleName, string> = {
  editor: 'Редактор',
  commenter: 'Комментатор',
  viewer: 'Только просмотр',
};

/**
 * Who may open this board — members, roles, invites, bans.
 *
 * Used from the dashboard card and from the in-board top bar. Access is a
 * stored grant: this panel either adds someone already in the organisation,
 * changes their role, kicks them out of the live session, or bans them so
 * group / org-wide paths cannot let them back in.
 */
export function BoardAccessPanel({
  boardId,
  boardName,
  orgId,
  /** Compact trigger for the board top bar. */
  compact,
}: {
  boardId: string;
  boardName: string;
  orgId: string;
  compact?: boolean;
}) {
  const [access, setAccess] = useState<BoardAccessView | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrgMemberView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickedUser, setPickedUser] = useState('');
  const [role, setRole] = useState<BoardRoleName>('editor');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [callBans, setCallBans] = useState<CallBanView[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [accessView, members, bans] = await Promise.all([
        api.boardAccess(boardId),
        authApi.listMembers(orgId).catch(() => [] as OrgMemberView[]),
        // Only call moderators may list these; for anyone else it is a 403.
        api.listCallBans(boardId).catch(() => [] as CallBanView[]),
      ]);
      setAccess(accessView);
      setOrgMembers(members);
      setCallBans(bans);
    } catch {
      setError('Не удалось загрузить доступ');
    } finally {
      setLoading(false);
    }
  }, [boardId, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const copyLink = async (token: string) => {
    const url = inviteUrl(token);
    setLastUrl(url);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard blocked; the link is shown below to copy by hand.
    }
    setCopiedToken(token);
    setTimeout(() => setCopiedToken((t) => (t === token ? null : t)), 1600);
  };

  const addMember = async () => {
    if (!pickedUser) return;
    setBusyId(pickedUser);
    try {
      await api.addBoardMember(boardId, pickedUser, role);
      setPickedUser('');
      await load();
    } catch {
      setError('Не удалось выдать доступ');
    } finally {
      setBusyId(null);
    }
  };

  const setMemberRole = async (userId: string, next: BoardRoleName) => {
    setBusyId(userId);
    try {
      await api.addBoardMember(boardId, userId, next);
      await load();
    } catch {
      setError('Не удалось изменить роль');
    } finally {
      setBusyId(null);
    }
  };

  const kickMember = async (userId: string, name: string) => {
    if (!window.confirm(`Исключить ${name} с доски? Доступ будет снят, сессия закрыта.`)) return;
    setBusyId(userId);
    try {
      await api.removeBoardMember(boardId, userId);
      await load();
    } catch {
      setError('Не удалось исключить');
    } finally {
      setBusyId(null);
    }
  };

  const banMember = async (userId: string, name: string) => {
    if (
      !window.confirm(
        `Забанить ${name} на этой доске? Повторный вход будет запрещён, пока бан не снимут.`,
      )
    ) {
      return;
    }
    setBusyId(userId);
    try {
      await api.banBoardMember(boardId, userId);
      await load();
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'cannot_ban_staff'
          ? 'Нельзя забанить учителя, куратора или администратора'
          : 'Не удалось забанить',
      );
    } finally {
      setBusyId(null);
    }
  };

  const unbanFromCall = async (userId: string) => {
    setBusyId(userId);
    try {
      await api.unbanFromCall(boardId, userId);
      setCallBans((list) => list.filter((b) => b.userId !== userId));
    } catch {
      setError('Не удалось вернуть доступ к звонку');
    } finally {
      setBusyId(null);
    }
  };

  const unbanMember = async (userId: string) => {
    setBusyId(userId);
    try {
      await api.unbanBoardMember(boardId, userId);
      await load();
    } catch {
      setError('Не удалось снять бан');
    } finally {
      setBusyId(null);
    }
  };

  const createLink = async () => {
    try {
      const invite = await api.createBoardInvite(boardId, role);
      await load();
      await copyLink(invite.token);
    } catch {
      setError('Не удалось создать приглашение');
    }
  };

  const revokeLink = async (token: string) => {
    try {
      await api.revokeBoardInvite(boardId, token);
      await load();
    } catch {
      setError('Не удалось отозвать приглашение');
    }
  };

  const bannedIds = new Set(access?.bans.map((b) => b.userId) ?? []);
  const candidates = orgMembers.filter(
    (m) =>
      m.userId !== access?.creator?.userId &&
      !access?.members.some((existing) => existing.userId === m.userId) &&
      !bannedIds.has(m.userId),
  );

  return (
    <Popover
      align="right"
      title={`Участники «${boardName}»`}
      trigger={() =>
        compact ? (
          <>
            <Icon name="users" size={16} />
            <span className="btn-label">Участники</span>
          </>
        ) : (
          <Icon name="users" size={15} />
        )
      }
    >
      {() => (
        <div className="popover-stack org-members-popover board-access-popover">
          <div className="popover-label">Участники доски</div>

          {loading && <div className="org-members-hint">Загрузка…</div>}
          {error && <div className="org-members-error">{error}</div>}

          <ul className="org-members-list">
            {access?.creator && (
              <li className="org-member-row">
                <span className="org-member-avatar" style={{ background: access.creator.color }}>
                  {access.creator.name.slice(0, 1).toUpperCase()}
                </span>
                <span className="org-member-text">
                  <span className="org-member-name">{access.creator.name}</span>
                  <span className="org-member-email">Владелец доски</span>
                </span>
              </li>
            )}
            {access?.members.map((member) => (
              <li key={member.userId} className="org-member-row">
                <span className="org-member-avatar" style={{ background: member.color }}>
                  {member.name.slice(0, 1).toUpperCase()}
                </span>
                <span className="org-member-text">
                  <span className="org-member-name">{member.name}</span>
                  {access.canManage ? (
                    <select
                      className="org-member-role board-member-role"
                      value={member.role}
                      disabled={busyId === member.userId}
                      aria-label={`Роль: ${member.name}`}
                      onChange={(e) =>
                        void setMemberRole(member.userId, e.target.value as BoardRoleName)
                      }
                    >
                      <option value="editor">Редактор</option>
                      <option value="commenter">Комментатор</option>
                      <option value="viewer">Только просмотр</option>
                    </select>
                  ) : (
                    <span className="org-member-email">{ROLE_LABEL[member.role]}</span>
                  )}
                </span>
                {access.canManage && (
                  <span className="board-member-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Исключить: ${member.name}`}
                      data-tip="Исключить"
                      disabled={busyId === member.userId}
                      onClick={() => void kickMember(member.userId, member.name)}
                    >
                      <Icon name="close" size={13} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn is-danger"
                      aria-label={`Забанить: ${member.name}`}
                      data-tip="Забанить"
                      disabled={busyId === member.userId}
                      onClick={() => void banMember(member.userId, member.name)}
                    >
                      <Icon name="shield" size={13} />
                    </button>
                  </span>
                )}
              </li>
            ))}
            {!loading && !access?.creator && access?.members.length === 0 && (
              <li className="org-members-hint">Доска организации — открыта всем сотрудникам.</li>
            )}
          </ul>

          {access?.canManage && (access.bans.length ?? 0) > 0 && (
            <>
              <div className="panel-divider" />
              <div className="popover-label">Забанены</div>
              <ul className="org-members-list">
                {access.bans.map((ban) => (
                  <li key={ban.userId} className="org-member-row">
                    <span className="org-member-avatar" style={{ background: ban.color }}>
                      {ban.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="org-member-text">
                      <span className="org-member-name">{ban.name}</span>
                      <span className="org-member-email">Нет доступа</span>
                    </span>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Разбанить: ${ban.name}`}
                      data-tip="Снять бан"
                      disabled={busyId === ban.userId}
                      onClick={() => void unbanMember(ban.userId)}
                    >
                      <Icon name="check" size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {callBans.length > 0 && (
            <>
              <div className="panel-divider" />
              <div className="popover-label">Нет доступа к звонку</div>
              <ul className="org-members-list">
                {callBans.map((ban) => (
                  <li key={ban.userId} className="org-member-row">
                    <span className="org-member-avatar" style={{ background: ban.color }}>
                      {ban.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="org-member-text">
                      <span className="org-member-name">{ban.name}</span>
                      <span className="org-member-email">
                        {ban.reason === 'kick' ? 'Исключён' : 'Бан'}
                        {ban.until
                          ? ` до ${new Date(ban.until).toLocaleString('ru-RU', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}`
                          : ''}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Вернуть доступ к звонку: ${ban.name}`}
                      data-tip="Вернуть доступ к звонку"
                      disabled={busyId === ban.userId}
                      onClick={() => void unbanFromCall(ban.userId)}
                    >
                      <Icon name="check" size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {access?.canManage && (
            <>
              <div className="panel-divider" />
              <div className="popover-label">Пригласить</div>

              <div className="board-invite-form">
                <select
                  className="org-member-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as BoardRoleName)}
                >
                  <option value="editor">Редактор</option>
                  <option value="commenter">Комментатор</option>
                  <option value="viewer">Только просмотр</option>
                </select>

                <select
                  className="org-member-role"
                  value={pickedUser}
                  onChange={(e) => setPickedUser(e.target.value)}
                >
                  <option value="">Из организации…</option>
                  {candidates.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="menu-item org-invite-btn"
                  disabled={!pickedUser || busyId !== null}
                  onClick={() => void addMember()}
                >
                  <Icon name="plus" size={15} /> Дать доступ
                </button>
                <button
                  type="button"
                  className="menu-item org-invite-btn"
                  onClick={() => void createLink()}
                >
                  <Icon name="link" size={15} /> Ссылка-приглашение
                </button>
              </div>

              {lastUrl && (
                <input
                  className="tag-input"
                  readOnly
                  value={lastUrl}
                  aria-label="Ссылка-приглашение"
                  onFocus={(e) => e.currentTarget.select()}
                />
              )}

              {(access?.invites.length ?? 0) > 0 && (
                <ul className="org-invite-list">
                  {access?.invites.map((invite) => (
                    <li key={invite.token} className="org-invite-row">
                      <button
                        type="button"
                        className="menu-item"
                        onClick={() => void copyLink(invite.token)}
                      >
                        <Icon name={copiedToken === invite.token ? 'check' : 'link'} size={14} />
                        <span>{ROLE_LABEL[invite.role]}</span>
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label="Отозвать ссылку"
                        data-tip="Отозвать"
                        onClick={() => void revokeLink(invite.token)}
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </Popover>
  );
}
